/* eslint-disable camelcase */
import i18next from 'i18next';
import { createSelector } from 'reselect';

import { isAuthenticated } from 'actions/user';
import { getBalanceEquity } from 'components/holdings_balance/selectorHelper';
import {
  calculateStopLossEstPrice,
  calculateTakeProfitEstPrice,
  isValidTakeProfitValue,
  marketPlaceOrderConfirmationTable,
  spotMarketPlaceOrderConfirmationTable,
} from 'components/limit_place_order/helper';
import { calculateEstimatedTradingFee, priceToUse } from 'components/placeorder/helper';
import { ASSET_SYMBOL, MARGIN_TYPES, UNIT_OF_ASSETS_QUANTITY } from 'constants/constants';
import {
  MARGIN_MODE,
  ORDER_TYPE,
  PLACE_ORDER_TYPE,
  SCREEN_SIZES,
  SIDE,
} from 'constants/enums';
import { getDefaultCurrencyForNewUser } from 'helpers/currency';
import {
  calculateEstimatedMarketPrice,
  checkLowBalance,
  convertOrderBook,
  getNotional,
  round,
  calculateExtraOrderMargin,
} from 'helpers/formulas';
import { getNonSpotMaxQuantity } from 'helpers/getNonSpotMaxQuantity';
import { generateAssetUnitLabel } from 'helpers/localized';
import { division } from 'helpers/math';
import { gt, path, pick, pipe, sort } from 'helpers/ramda';
import {
  convertExponentialToDecimal,
  getBalanceBySymbol,
  isEmpty,
  isFutures,
  isNan,
  isOptions,
  isSpotContract,
  truncAfterDecimal,
} from 'helpers/utils';
import { impactSizeCheck, impactSizeCheckSpot, slippageCheck } from 'helpers/validations';
import { APP_DRAWER_TAB_ID_MAPPING } from 'reducers/initialstate/appDrawer';
import { markPriceState, spotPriceState } from 'selectors/priceSelectors';
import {
  getPrefilledValuesState,
  leverageState,
  openBuyOrdersState,
  openPositionState,
  openSellOrdersState,
  orderMarginSelector,
  OrderMarginState,
  selectedProductSelector,
  tradingCreditsSelector,
} from 'selectors/tradeSelectors';
import { selectedProductPriceBandSelector } from 'stores/selectedProduct/selectedProduct.selectors';
// import {
//   allMarkPriceSelector,
//   allSpotPriceSelector,
// } from 'stores/ticker/ticker.selectors';
import { ContractType, NotionalType } from 'types/IProducts';
import IState from 'types/Istore';
import { QuantityDropdownPreference } from 'types/IUser';

// eslint-disable-next-line import/no-cycle
import {
  convertNumberOfContractsToSelectedCurrency,
  getSelectedProductBalance,
  hintTextToShowInQuantityInput,
  positionSizeForSelectedProduct,
} from '../formulae';
import {
  aggregateSpotQuoteNotional,
  aggregateSpotUnderlyingNotional,
  calculateSpotEstimatedMarketPriceUsingUnderlyingSize,
  calculateSpotImpactPrice,
  computeSpotMaxQuantity,
  getSpotTotalCost,
} from '../formulae/spotFormulae';
import { allOpenPositionsSelector } from './holdingsSelectors';
import { l2OrderbookSelectorWithDepth } from './l2OrderbookSelector';
import { activeOffersSelector, appliedOffersSelector } from './offersSelector';
import {
  tradePreferencesSelector,
  userMarginModeSelector,
  userPreferencesSelector,
} from './otherSelectors';
import {
  balanceBySymbolSelector,
  balanceSelector,
  multiCollateralSelector,
} from './walletSelector';

export enum QuantityDropDownType {
  QUOTING = 'quoting_asset',
  SETTLING = 'settling_asset',
  UNDERLYING = 'underlying_asset',
  CONT = 'cont',
}

// Selector to check if user is on mobile v2 chart screen
// This is memoized separately to avoid triggering generateOrderSelector on every tab change
export const isOnMobileV2ChartScreenSelector = createSelector(
  [(state: IState) => state.appDrawer],
  appDrawer => {
    const isMobile = typeof window !== 'undefined' && window.innerWidth < SCREEN_SIZES.MD;
    return isMobile && appDrawer.activeTab === APP_DRAWER_TAB_ID_MAPPING.CHART_ORDERBOOK;
  }
);

export const placeOrderSelector = (state: IState) => state.placeOrder;

/**
 * Form States
 */
export const sideSelection = (state: IState) => state.placeOrder?.side;
export const orderTypeSelection = (state: IState) => state.placeOrder?.orderType;
export const orderSourceSelector = (state: IState) => state.placeOrder.orderSource;
export const quantityInputDropdownValue = (state: IState) =>
  state.placeOrder?.dropdownValue;
export const quantityInputValue = (state: IState) => {
  const value = state.placeOrder?.quantityInputValue;
  // Return null if it's an empty object
  return value && typeof value === 'object' && Object.keys(value).length === 0
    ? null
    : value;
};
export const limitPriceSelector = (state: IState) => state.placeOrder?.limitPrice;
export const reduceOnlySelector = (state: IState) => state.placeOrder?.reduceOnly;
export const closePositionConfirmationModalIsOpenSelector = (state: IState) =>
  state.placeOrder?.closePositionConfirmationModalIsOpen; // TODO
export const takeProfitPercentSelector = (state: IState) =>
  state.placeOrder?.bracketOrderData?.takeProfit?.triggerPrice?.percentage;
export const stopLossPercentSelector = (state: IState) =>
  state.placeOrder?.bracketOrderData?.stopLoss?.triggerPrice?.percentage;
export const showConfirmationPopupSelector = (state: IState) =>
  state.placeOrder?.showConfirmationPopup;
export const quantityInputErrorMessageSelector = (state: IState) =>
  state.placeOrder?.quantityInputErrorMessage;
export const dontShowConfirmationCheckboxCheckedSelector = (state: IState) =>
  state.placeOrder?.dontShowConfirmationCheckboxChecked;
export const selectedPercentageSelector = (state: IState) =>
  state.placeOrder?.selectedPercentage;
export const dontShowConfirmationOnChartCheckedSelector = (state: IState) =>
  state.placeOrder?.dontShowConfirmationOnChartChecked;
export const orderSubmitStatusSelector = (state: IState) =>
  state.placeOrder?.orderSubmitStatus;
export const portfolioMarginValueSelector = (state: IState) =>
  state.placeOrder?.marginValue;
export const placeOrderQuantityInputHasErrorSelector = (state: IState) =>
  state.placeOrder?.quantityInputHasError;
export const userLeverageSelector = (state: IState) =>
  state.placeOrder?.userInputLeverage;
export const leverageMaxNotionalValueSelector = (state: IState) =>
  state.placeOrder?.leverageMaxNotionalValue;
export const userSelectedQuantityPercentageSelector = (state: IState) =>
  state.placeOrder?.userSelectedQuantityPercentage;
export const allConvertedCurrencyValuesSelector = (state: IState) =>
  state.placeOrder?.allConvertedCurrencyValues;
export const openingCommissionSelector = (state: IState) =>
  state.placeOrder?.openingCommission;
export const quantityInputHasAPIErrorSelector = (state: IState) =>
  state.placeOrder?.quantityHasAPIError;

export const decimalHandle = inputedQuantity => {
  switch (inputedQuantity?.toString?.()) {
    case '.':
    case '0.':
    case '.0':
      return 0;
    default:
      return inputedQuantity;
  }
};

/**
 * Calculations
 */
export const getTotalPositonSize = createSelector(
  [allOpenPositionsSelector, selectedProductSelector],
  positionSizeForSelectedProduct
);

export const selectedProductBalance = createSelector(
  [
    selectedProductSelector,
    balanceBySymbolSelector,
    userMarginModeSelector,
    tradingCreditsSelector,
    multiCollateralSelector,
    sideSelection,
  ],
  getSelectedProductBalance
);

export const optionsMaxQuantitySize = createSelector(
  [
    selectedProductSelector,
    l2OrderbookSelectorWithDepth,
    sideSelection,
    leverageState,
    selectedProductBalance,
    allOpenPositionsSelector,
    limitPriceSelector,
  ],
  (product, orderbooks, side, leverage, balance, allOpenPositions, limit_price) => {
    return getNonSpotMaxQuantity({
      product,
      balance: balance.balance,
      orderbooks,
      allOpenPositions,
      side,
      limit_price,
      leverage,
      initialLeverage: leverage, // for v2, initial leverage is same as leverage due to redux
    });
  }
);

export const primaryCurrencySelector = createSelector(
  [selectedProductSelector],
  (selectedProduct): string => {
    const contractType = selectedProduct?.contract_type;
    const quotingAsset = selectedProduct?.quoting_asset;
    const underlyingAsset = selectedProduct?.underlying_asset;
    const notionalType = selectedProduct?.notional_type;
    if (isSpotContract(contractType)) {
      return quotingAsset?.symbol ?? '';
    }
    if (isFutures(contractType) && notionalType === 'inverse') {
      return quotingAsset?.symbol ?? '';
    }
    return underlyingAsset?.symbol ?? '';
  }
);

export const secondaryCurrencySelector = createSelector(
  [selectedProductSelector],
  (selectedProduct): string => {
    const contractType = selectedProduct?.contract_type;
    const quotingAsset = selectedProduct?.quoting_asset;
    const underlyingAsset = selectedProduct?.underlying_asset;
    const notionalType = selectedProduct?.notional_type;
    const settlingAsset = selectedProduct?.settling_asset;
    if (isSpotContract(contractType)) {
      return underlyingAsset?.symbol ?? '';
    }
    if (isFutures(contractType)) {
      if (notionalType === 'inverse') {
        return underlyingAsset?.symbol ?? '';
      }
      return quotingAsset?.symbol ?? '';
    }
    return settlingAsset?.symbol ?? '';
  }
);

export const quantityInputValueInContract = createSelector(
  [
    allConvertedCurrencyValuesSelector,
    selectedProductSelector,
    quantityInputDropdownValue,
  ],
  (convertedCurrency, selectedProduct, dropDownValue) => {
    if (isSpotContract(selectedProduct?.contract_type)) {
      return convertedCurrency[dropDownValue];
    }
    return convertedCurrency?.Cont;
  }
);

export const spotMaxQuantititySize = createSelector(
  [
    balanceBySymbolSelector,
    getPrefilledValuesState,
    sideSelection,
    selectedProductSelector,
    quantityInputDropdownValue,
    l2OrderbookSelectorWithDepth,
    tradingCreditsSelector,
  ],
  (
    balanceState,
    prefilledValues,
    side,
    selectedProduct,
    dropdownValue,
    orderbook,
    assetIdTradingCreditsMapping
  ): number => {
    return (
      !isEmpty(balanceState) &&
      computeSpotMaxQuantity({
        balanceState,
        orderPrice: prefilledValues?.price ?? 0,
        side,
        product: selectedProduct,
        isQuoteAsset: dropdownValue === selectedProduct?.quoting_asset?.symbol,
        orderbook,
        assetIdTradingCreditsMapping,
      })
    );
  }
);

const getCurrencyForInverseNotionalType = (
  primaryCurrency: string,
  secondaryCurrency: string,
  preference: QuantityDropdownPreference
) => {
  switch (preference?.inverse) {
    case QuantityDropDownType.QUOTING:
      return primaryCurrency;
    case QuantityDropDownType.UNDERLYING:
      return secondaryCurrency;
    case QuantityDropDownType.CONT:
      return UNIT_OF_ASSETS_QUANTITY;
    default:
      return getDefaultCurrencyForNewUser(primaryCurrency);
  }
};

const getCurrencyForVanillaNotionalType = (
  primaryCurrency: string,
  secondaryCurrency: string,
  preference: QuantityDropdownPreference,
  isSecondaryCurrencyAssetSymbol: boolean
) => {
  if (isSecondaryCurrencyAssetSymbol) {
    if (preference?.vanilla?.usdt_settled) {
      switch (preference?.vanilla?.usdt_settled) {
        case QuantityDropDownType.SETTLING:
          return secondaryCurrency;
        case QuantityDropDownType.UNDERLYING:
          return primaryCurrency;
        case QuantityDropDownType.CONT:
          return UNIT_OF_ASSETS_QUANTITY;
        default:
          return getDefaultCurrencyForNewUser(primaryCurrency);
      }
    } else {
      return getDefaultCurrencyForNewUser(primaryCurrency);
    }
  }

  if (preference?.vanilla?.non_usdt_settled) {
    switch (preference?.vanilla?.non_usdt_settled) {
      case QuantityDropDownType.SETTLING:
        return secondaryCurrency;
      case QuantityDropDownType.UNDERLYING:
        return primaryCurrency;
      case QuantityDropDownType.CONT:
        return UNIT_OF_ASSETS_QUANTITY;
      default:
        return getDefaultCurrencyForNewUser(primaryCurrency);
    }
  } else {
    return getDefaultCurrencyForNewUser(primaryCurrency);
  }
};

export const defaultCurrencySelector = createSelector(
  [
    selectedProductSelector,
    userPreferencesSelector,
    primaryCurrencySelector,
    secondaryCurrencySelector,
  ],
  (selectedProduct, userPreference, primaryCurrency, secondaryCurrency) => {
    const quantityDropdownPrefrence = userPreference?.quantity_dropdown_preference
      ? { ...userPreference.quantity_dropdown_preference }
      : {
          inverse: null,
          vanilla: {
            usdt_settled: null,
            non_usdt_settled: null,
          },
        };
    const notionalType = selectedProduct?.notional_type;
    if (notionalType === NotionalType.Inverse) {
      return getCurrencyForInverseNotionalType(
        primaryCurrency,
        secondaryCurrency,
        quantityDropdownPrefrence
      );
    }

    if (notionalType === NotionalType.Vanilla) {
      const isSecondaryCurrencyAssetSymbol = secondaryCurrency === ASSET_SYMBOL;
      return getCurrencyForVanillaNotionalType(
        primaryCurrency,
        secondaryCurrency,
        quantityDropdownPrefrence,
        isSecondaryCurrencyAssetSymbol
      );
    }

    return getDefaultCurrencyForNewUser(primaryCurrency);
  }
);

// Function to determine the value based on asset and notional type
export const valueForNotionalTypeSelector = createSelector(
  [primaryCurrencySelector, selectedProductSelector, quantityInputDropdownValue],
  (primaryCurrency, selectedProduct, asset) => {
    const notionalType = selectedProduct?.notional_type;

    if (asset === UNIT_OF_ASSETS_QUANTITY) return QuantityDropDownType.CONT;
    if (notionalType === NotionalType.Inverse) {
      return asset === primaryCurrency
        ? QuantityDropDownType.QUOTING
        : QuantityDropDownType.UNDERLYING;
    }
    return asset === primaryCurrency
      ? QuantityDropDownType.UNDERLYING
      : QuantityDropDownType.SETTLING;
  }
);

// updates the user preference based on the assest he selects
export const quantityCurrencyInPreferencesSelector = createSelector(
  [userPreferencesSelector, valueForNotionalTypeSelector, selectedProductSelector],
  (userPreference, value, selectedProduct) => {
    const notionalType = selectedProduct?.notional_type;
    const settlingAsset = selectedProduct?.settling_asset;

    // Clone the existing preferences deeply as needed
    const quantityDropdownPreference = userPreference?.quantity_dropdown_preference || {};
    const preferencesUpdate = {
      preferences: {
        quantity_dropdown_preference: {
          inverse: quantityDropdownPreference.inverse ?? null,
          vanilla: quantityDropdownPreference.vanilla ?? {
            usdt_settled: null,
            non_usdt_settled: null,
          },
        },
      },
    };

    if (notionalType === NotionalType.Inverse) {
      preferencesUpdate.preferences.quantity_dropdown_preference.inverse = value;
    } else if (notionalType === NotionalType.Vanilla) {
      if (settlingAsset?.symbol === ASSET_SYMBOL) {
        preferencesUpdate.preferences.quantity_dropdown_preference.vanilla.usdt_settled =
          value;
      } else {
        // eslint-disable-next-line max-len
        preferencesUpdate.preferences.quantity_dropdown_preference.vanilla.non_usdt_settled =
          value;
      }
    }
    return preferencesUpdate;
  }
);

export const calculatedQuantityOnDropdownChangeSelector = createSelector(
  [
    quantityInputDropdownValue,
    selectedProductSelector,
    quantityInputValueInContract,
    getPrefilledValuesState,
    sideSelection,
    (state, valueFromPercent) => valueFromPercent,
  ],
  (asset, selectedProduct, qty, prefilledQuantity, side, valueFromPercent) =>
    convertNumberOfContractsToSelectedCurrency(
      valueFromPercent ?? qty ?? prefilledQuantity,
      selectedProduct,
      asset,
      side
    )
);

export const calculatedQuantityOnPercentageChangeSelector = createSelector(
  [
    quantityInputDropdownValue,
    selectedProductSelector,
    quantityInputValueInContract,
    getPrefilledValuesState,
    sideSelection,
    (state, valueFromPercent) => valueFromPercent,
  ],
  (asset, selectedProduct, qty, prefilledQuantity, side, valueFromPercent) =>
    convertNumberOfContractsToSelectedCurrency(
      valueFromPercent ?? qty ?? prefilledQuantity,
      selectedProduct,
      asset,
      side
    )
);

export const quantityInputPrefilledValuesSelector = createSelector(
  [
    selectedProductSelector,
    getPrefilledValuesState,
    l2OrderbookSelectorWithDepth,
    sideSelection,
    quantityInputDropdownValue,
  ],
  (selectedProduct, prefilledValues, orderbook, side, dropDownValue) => {
    if (isNan(prefilledValues?.size) || Number(prefilledValues?.size) <= 0) {
      return 0;
    }
    const contractType = selectedProduct?.contract_type;
    // if contract type is not spot, no need to compute further
    if (contractType !== 'spot') {
      return prefilledValues?.size;
    }

    // check if dropdown value is quoting symbol
    const quotingSymbol = selectedProduct?.quoting_asset?.symbol;
    const isQuoteAsset = contractType === 'spot' && dropDownValue === quotingSymbol;

    if (!isQuoteAsset) {
      return prefilledValues?.size;
    }

    const impactPrice =
      calculateSpotImpactPrice(orderbook, side, prefilledValues?.size, isQuoteAsset) ?? 0;
    const size = truncAfterDecimal(
      convertExponentialToDecimal(
        aggregateSpotQuoteNotional(impactPrice, orderbook, prefilledValues?.size, side)
      ),
      4
    );
    return size;
  }
);

export const inputValueBasedOnPercentageSelector = createSelector(
  [
    optionsMaxQuantitySize,
    spotMaxQuantititySize,
    selectedProductSelector,
    (state, percentVal) => percentVal,
  ],
  (maxSize, spotMaxSize, selectedProduct, percentage) => {
    if (maxSize === null) {
      return null;
    }
    if (selectedProduct?.contract_type === ContractType.Spot) {
      return truncAfterDecimal(convertExponentialToDecimal(spotMaxSize * percentage), 5);
    }
    return truncAfterDecimal(percentage * maxSize, 0);
  }
);

export const openBuyOrdersSelector = createSelector(
  [openBuyOrdersState],
  openBuyOrdersRaw =>
    pipe(
      () => openBuyOrdersRaw,
      orders => orders.map(pick(['unfilled_size', 'limit_price'])),
      sort((a, b) => Number(b?.limit_price ?? 0) - Number(a?.limit_price ?? 0))
    )()
);

export const openSellOrdersSelector = createSelector(
  [openSellOrdersState],
  openSellOrdersRaw =>
    pipe(
      () => openSellOrdersRaw,
      orders => orders.map(pick(['unfilled_size', 'limit_price'])),
      sort((a, b) => Number(a.limit_price) - Number(b.limit_price))
    )()
);

export const marginCalculationSelector = createSelector(
  [
    selectedProductSelector,
    userMarginModeSelector,
    openPositionState,
    leverageState,
    limitPriceSelector,
    quantityInputValueInContract,
    sideSelection,
    l2OrderbookSelectorWithDepth,
    markPriceState,
    spotPriceState,
    orderMarginSelector,
    openBuyOrdersSelector,
    openSellOrdersSelector,
  ],
  (
    selectedProduct,
    marginMode,
    position,
    leverage,
    orderPrice,
    orderSize,
    side,
    orderbooks,
    markPrice,
    spotPrice,
    orderMargins,
    openBuyOrders,
    openSellOrders
  ) => {
    if (marginMode === MARGIN_TYPES.PORTFOLIO || !orderSize) {
      return null; // Or some default state indicating not to proceed
    }

    const size = Number(orderSize) ?? 0;
    const orderMargin = Number(
      orderMargins[selectedProduct?.id ?? '']?.order_margin ?? 0
    );

    const price = isOptions(selectedProduct?.contract_type)
      ? calculateEstimatedMarketPrice(
          size,
          convertOrderBook(orderbooks, side),
          selectedProduct?.tick_size ?? 0,
          selectedProduct?.notional_type ?? NotionalType.Vanilla,
          markPrice
        )
      : markPrice;

    const result = {
      mark_price: price,
      product: selectedProduct,
      position: position || 0,
      leverage,
      order_margin: orderMargin,
      user_buy_orders: openBuyOrders,
      user_sell_orders: openSellOrders,
      side,
      size,
      limit_price: orderPrice,
      spot_price: spotPrice,
    };
    return result;
  }
);

export const estimatedExecPriceSelector = createSelector(
  [
    selectedProductSelector,
    l2OrderbookSelectorWithDepth,
    sideSelection,
    quantityInputValueInContract,
    quantityInputDropdownValue,
    markPriceState,
  ],
  (selectedProduct, orderbook, side, orderSize, dropDownValue, markPrice) => {
    if (isNan(orderSize) || Number(orderSize) <= 0) {
      return 0;
    }
    const orders = convertOrderBook(orderbook, side);
    const contractType = selectedProduct?.contract_type;

    // check if dropdown value is quoting symbol
    const quotingSymbol = selectedProduct?.quoting_asset?.symbol;
    const isQuoteAsset =
      contractType === ContractType.Spot && dropDownValue === quotingSymbol;

    if (selectedProduct?.contract_type === ContractType.Spot) {
      const impactPrice = calculateSpotImpactPrice(
        orderbook,
        side,
        Number(orderSize),
        isQuoteAsset
      );
      const size = isQuoteAsset
        ? aggregateSpotUnderlyingNotional(
            impactPrice ?? 0,
            orderbook,
            orderSize ?? 0,
            side
          )
        : orderSize;
      return calculateSpotEstimatedMarketPriceUsingUnderlyingSize(size, orders);
    }
    return calculateEstimatedMarketPrice(
      Number(orderSize ?? 0),
      orders,
      selectedProduct?.tick_size,
      selectedProduct?.notional_type,
      markPrice
    );
  }
);

export const takeProfitEstPriceSelector = createSelector(
  [
    estimatedExecPriceSelector,
    takeProfitPercentSelector,
    sideSelection,
    selectedProductSelector,
  ],
  (estimatedExecPrice, takeProfitPercent, side, selectedProduct) =>
    calculateTakeProfitEstPrice({
      val: takeProfitPercent,
      side,
      orderPrice: estimatedExecPrice,
      tick_size: selectedProduct?.tick_size,
    })
);

export const stopLossEstPriceSelector = createSelector(
  [
    estimatedExecPriceSelector,
    stopLossPercentSelector,
    sideSelection,
    selectedProductSelector,
  ],
  (estimatedExecPrice, stopLossPercent, side, selectedProduct) =>
    calculateStopLossEstPrice({
      val: stopLossPercent,
      side,
      orderPrice: estimatedExecPrice,
      tick_size: selectedProduct?.tick_size,
    })
);

export const priceToUseSelector = createSelector(
  [
    markPriceState,
    selectedProductSelector,
    sideSelection,
    quantityInputValueInContract,
    l2OrderbookSelectorWithDepth,
    limitPriceSelector,
    orderTypeSelection,
  ],
  (markPrice, selectedProduct, side, orderSize, orderBook, orderPrice, orderType) => {
    const priceBand = selectedProductPriceBandSelector();

    return priceToUse(
      selectedProduct,
      side,
      orderSize,
      markPrice,
      orderBook,
      orderPrice,
      orderType,
      priceBand
    );
  }
);

export const estimatedTradingFeeSelector = createSelector(
  [
    selectedProductSelector,
    quantityInputValueInContract,
    sideSelection,
    spotPriceState,
    orderTypeSelection,
    estimatedExecPriceSelector,
    priceToUseSelector,
    openPositionState,
  ],
  (
    selectedProduct,
    size,
    side,
    spotPrice,
    orderType,
    estimatedExecutionPrice,
    price,
    position
  ) => {
    const calculatedEstimatedTradingFeeData = calculateEstimatedTradingFee({
      price,
      contract_size: selectedProduct?.contract_value,
      size,
      settling_asset: selectedProduct?.settling_asset,
      premiumCommissionRate: selectedProduct?.product_specs?.premium_commission_rate,
      spotPrice,
      maker_commission: selectedProduct?.maker_commission_rate,
      taker_commission: selectedProduct?.taker_commission_rate,
      postOnly: false,
      side,
      contractType: selectedProduct?.contract_type,
      orderType,
      estimatedExecPrice: estimatedExecutionPrice,
      notional_type: selectedProduct?.notional_type,
      position,
    });

    return calculatedEstimatedTradingFeeData.unformattedEstTradingFees || 0;
  }
);

/*
 * PLEASE NOTE:- rectified marginValueSelector which handles position offesting as well.
 * unified calculations across both order placement via forms and charts
 */
export const marginValueSelector = createSelector(
  [
    quantityInputValueInContract,
    limitPriceSelector,
    sideSelection,
    leverageState,
    userMarginModeSelector,
    portfolioMarginValueSelector,
    selectedProductSelector,
    reduceOnlySelector,
    priceToUseSelector,
    spotPriceState,
    OrderMarginState,
    openBuyOrdersSelector,
    openSellOrdersSelector,
    openPositionState,
  ],
  (
    size,
    limitPrice,
    side,
    leverage,
    marginMode,
    portfolioMarginValue,
    selectedProduct,
    reduceOnly,
    price,
    spotPrice,
    selectedProductOrderMargin,
    openBuyOrders,
    openSellOrders,
    position
  ) => {
    if (!size) return 0; // initial page load & no input value
    if (marginMode === MARGIN_TYPES.PORTFOLIO) {
      return portfolioMarginValue;
    }
    const precision = selectedProduct?.settling_asset?.minimum_precision;

    if (reduceOnly) {
      return round(0, precision ?? 2);
    }

    const orderMargin = Number(selectedProductOrderMargin ?? 0);

    const result = calculateExtraOrderMargin({
      mark_price: price ?? 0,
      product: selectedProduct ?? {},
      position: position ?? 0,
      leverage,
      order_margin: orderMargin,
      user_buy_orders: openBuyOrders,
      user_sell_orders: openSellOrders,
      side,
      size: Number(size) ?? 0,
      limit_price: limitPrice,
      spot_price: spotPrice ?? 0,
    });
    return result;
  }
);

// margin including opening and closing leg fees.
export const marginWithBothLegFeesSelector = createSelector(
  [
    marginValueSelector,
    estimatedTradingFeeSelector,
    portfolioMarginValueSelector,
    userMarginModeSelector,
    reduceOnlySelector,
    selectedProductSelector,
  ],
  (
    margin,
    estimatedTradingFee,
    portfolioMarginValue,
    marginMode,
    reduceOnly,
    selectedProduct
  ) => {
    if (marginMode === MARGIN_TYPES.PORTFOLIO) {
      return portfolioMarginValue;
    }
    const precision = selectedProduct?.settling_asset?.minimum_precision;
    if (reduceOnly) {
      return round(0, precision ?? 2);
    }
    const result = Number(margin) + 2 * Number(estimatedTradingFee);
    return result;
  }
);

export const generateOrderSelector = createSelector(
  [
    orderTypeSelection,
    quantityInputValueInContract,
    sideSelection,
    selectedProductSelector,
    reduceOnlySelector,
    quantityInputDropdownValue,
    closePositionConfirmationModalIsOpenSelector,
    (state: IState) => state.placeOrder.bracketOrderData,
    isOnMobileV2ChartScreenSelector,
    orderSourceSelector,
  ],
  (
    orderType,
    orderSize,
    side,
    selectedProduct,
    reduceOnly,
    dropDownValue,
    confirmationModalIsOpen,
    bracketOrderData,
    isOnMobileV2ChartScreen,
    orderSource
  ) => {
    if (isNan(orderSize) || Number(orderSize) <= 0) {
      return null;
    }

    const order: any = {
      // ordertype will always be market for mobile view chart screen order placement
      order_type: isOnMobileV2ChartScreen
        ? ORDER_TYPE.MARKET
        : ORDER_TYPE[orderType?.toUpperCase()],
      side,
      product_id: selectedProduct?.id,
      reduce_only: reduceOnly.toString(),
      order_source: orderSource,
    };
    const contractType = selectedProduct?.contract_type;
    const quotingSymbol = selectedProduct?.quoting_asset?.symbol;
    const isQuoteAsset = contractType === 'spot' && dropDownValue === quotingSymbol;
    if (selectedProduct?.contract_type === ContractType.Spot && isQuoteAsset) {
      order.quote_size = Number(orderSize);
    } else {
      order.size = Number(orderSize);
    }

    // Advanced bracket order fields from bracket order data
    if (bracketOrderData?.takeProfit?.triggerPrice?.value) {
      order.bracket_take_profit_price = bracketOrderData?.takeProfit?.triggerPrice?.value;
    }
    if (bracketOrderData?.takeProfit?.limitPrice?.value) {
      order.bracket_take_profit_limit_price =
        bracketOrderData?.takeProfit?.limitPrice?.value;
    }
    if (bracketOrderData?.stopLoss?.triggerPrice?.value) {
      order.bracket_stop_loss_price = bracketOrderData?.stopLoss?.triggerPrice?.value;
    }
    if (bracketOrderData?.stopLoss?.limitPrice?.value) {
      order.bracket_stop_loss_limit_price = bracketOrderData?.stopLoss?.limitPrice?.value;
    }
    if (bracketOrderData?.stopLoss?.trailAmount?.value) {
      order.bracket_trail_amount = bracketOrderData?.stopLoss?.trailAmount?.value;
    }
    if (bracketOrderData?.triggerIndex?.value) {
      order.bracket_stop_trigger_method = bracketOrderData?.triggerIndex?.value;
    }

    if (confirmationModalIsOpen) {
      order.cancel_orders_accepted = true;
    }

    return order;
  }
);

/**
 * Pure function to calculate account equity
 * @param balance - Wallet balance object
 * @param allOpenPositions - Array of open positions
 * @param portfolioMarginData - Portfolio margin data
 * @param getMarkPrice - Function to get mark price by product ID (can be reactive from Zustand)
 * @returns Account equity value
 */
export const calculateAccountEquity = (
  balance: any,
  allOpenPositions: any[],
  portfolioMarginData: any,
  getMarkPrice: (productId: number) => number | undefined,
  isAuthChannelDisconnected?: boolean
) => {
  const { positions_upl: positionsUpl, asset_id: portfolioEnabledAsset } =
    portfolioMarginData?.aggregate || {};

  if (balance?.USDT) {
    return getBalanceEquity(
      balance?.USDT,
      allOpenPositions,
      getMarkPrice,
      positionsUpl,
      portfolioEnabledAsset!,
      isAuthChannelDisconnected
    );
  }
  return 0;
};

/**
 * Pure function to determine if reduce-only banner should be shown
 * @param selectedProduct - Currently selected product
 * @param marginMode - User's margin mode
 * @param equity - Account equity value
 * @param pfThreshold - Portfolio threshold value
 * @returns Boolean indicating if banner should be shown
 */
export const shouldShowReduceOnlyBanner = (
  selectedProduct: any,
  marginMode: string,
  equity: number,
  pfThreshold: number
) => {
  return (
    selectedProduct?.contract_type !== ContractType.Spot &&
    marginMode === MARGIN_MODE.PORTFOLIO &&
    Number(equity) <= Number(pfThreshold)
  );
};

export const slippageCheckSelector = createSelector(
  [
    selectedProductSelector,
    quantityInputValueInContract,
    markPriceState,
    spotPriceState,
    sideSelection,
    l2OrderbookSelectorWithDepth,
    estimatedExecPriceSelector,
  ],
  (
    selectedProduct,
    size,
    markPrice,
    spotPrice,
    side,
    orderbooks,
    estimatedExecutionPrice
  ) => {
    const orders = convertOrderBook(orderbooks, side);
    const precision =
      selectedProduct?.contract_type === 'spot'
        ? selectedProduct?.quoting_asset?.minimum_precision
        : selectedProduct?.quoting_asset?.minimum_precision;

    const slippage = slippageCheck({
      estimatedExecutionPrice,
      markPrice,
      contractType: selectedProduct?.contract_type,
      precision,
      side,
      spotPrice,
      orders,
      size,
    });
    return slippage;
  }
);

export const spotLowBalanceErrorSelector = createSelector(
  [
    balanceBySymbolSelector,
    selectedProductSelector,
    sideSelection,
    quantityInputValue,
    l2OrderbookSelectorWithDepth,
    quantityInputDropdownValue,
  ],
  (balanceState, selectedProduct, side, size, orderbook, dropDownValue) => {
    const isBuyActionState = side === SIDE.BUY;
    const quotingSymbol = selectedProduct?.quoting_asset?.symbol;
    const contractType = selectedProduct?.contract_type;
    const isQuoteAsset = contractType === 'spot' && dropDownValue === quotingSymbol;
    const underlyingSymbol = selectedProduct?.underlying_asset?.symbol;

    if (contractType === ContractType.Spot && size) {
      const balanceSymbol = isBuyActionState ? quotingSymbol : underlyingSymbol;

      const price = calculateSpotImpactPrice(orderbook, side, size, isQuoteAsset);

      const { available_balance } = getBalanceBySymbol(balanceSymbol, balanceState);

      const totalCost = getSpotTotalCost(price, size, isQuoteAsset, isBuyActionState);
      return Number(totalCost) > Number(available_balance);
    }
    return false;
  }
);

export const lowBalanceErrorSelector = createSelector(
  [
    balanceBySymbolSelector,
    selectedProductSelector,
    userMarginModeSelector,
    sideSelection,
    multiCollateralSelector,
    marginValueSelector,
    spotLowBalanceErrorSelector,
    estimatedTradingFeeSelector,
  ],
  (
    balanceState,
    selectedProduct,
    marginMode,
    side,
    multiCollateralData,
    margin,
    spotLowBalanceError,
    estimatedTradingFee
  ) => {
    // For spot orders, return the spot balance error directly it will decide to return true or false
    // else it will return the low balance error where it will fail usecase like (WEB-6808)
    // atleast one position is taken and without refresh if we try to place spot order
    if (selectedProduct?.contract_type === 'spot') {
      return spotLowBalanceError;
    }

    const isBuyActionState = side === SIDE.BUY;

    const { available_margin_usdt, available_margin_long_options } = multiCollateralData;
    /**
     * returns true if
     * - multiCollateralData is less than margin [ MarginMode = CROSS ]
     * - balanceState[symbol]?.available_balance is less than margin [ MarginMode != CROSS ]
     */
    /**
     * - Margin should include both opening and closing leg fees
     * - For portfolio mode, margin is nothing but margin along with both open and leg fees, which backend sends and returned by marginValue selector
     * - For cross and isolated margin modes we need to explicitly add opening and closing fees to the margin value received from the marginValue selector
     */
    const effectiveMarginValue =
      marginMode === MARGIN_TYPES.PORTFOLIO
        ? margin
        : Number(margin) + 2 * estimatedTradingFee;
    const marginValue = String(effectiveMarginValue);
    const availableBalUSDT =
      isBuyActionState && isOptions(selectedProduct?.contract_type)
        ? available_margin_long_options
        : available_margin_usdt;
    const lowBalance = checkLowBalance(
      selectedProduct ?? {},
      marginValue,
      balanceState,
      marginMode,
      availableBalUSDT
    );
    return lowBalance;
  }
);

export const quantityInputHasErrorSelector = createSelector(
  [
    placeOrderQuantityInputHasErrorSelector,
    quantityInputErrorMessageSelector,
    lowBalanceErrorSelector,
    userMarginModeSelector,
    quantityInputValueInContract,
    portfolioMarginValueSelector,
  ],
  (
    quantityInputHasError,
    errorMessage,
    lowBalanceError,
    marginType,
    orderSize,
    portfolioMarginValue
  ) => {
    const hasExistingError = quantityInputHasError || !!errorMessage || lowBalanceError;

    // Disable button while portfolio margin API is calculating estimated margin
    // But enable it once error is set, so user can see the error message
    const isPortfolioMarginPending =
      marginType === MARGIN_TYPES.PORTFOLIO &&
      Number(orderSize) > 0 &&
      portfolioMarginValue === null &&
      !quantityInputHasError &&
      !errorMessage &&
      !lowBalanceError;

    return hasExistingError || isPortfolioMarginPending;
  }
);

export const isFormValidSelector = createSelector(
  [
    selectedProductSelector,
    leverageState,
    quantityInputValueInContract,
    lowBalanceErrorSelector,
    reduceOnlySelector,
    l2OrderbookSelectorWithDepth,
    sideSelection,
    takeProfitEstPriceSelector,
    estimatedExecPriceSelector,
    quantityInputHasErrorSelector,
  ],
  (
    selectedProduct,
    leverage,
    qtyInputValueInCont,
    showLowBalanceMessage,
    reduceOnly,
    orderbooks,
    side,
    takeProfitEstPrice,
    orderPrice,
    quantityInputHasError
  ) => {
    if (quantityInputHasError) {
      return false;
    }
    const orderSize = qtyInputValueInCont;
    const orders = convertOrderBook(orderbooks, side);
    const maxValue = 100 / parseInt(selectedProduct?.initial_margin ?? '0', 10);

    if (!reduceOnly && (showLowBalanceMessage || isEmpty(orders))) {
      return false;
    }

    if (!orderSize || Number(orderSize) <= 0) {
      return false;
    }

    if (
      (typeof leverage === 'undefined' ||
        Number(leverage) === 0 ||
        String(leverage) === '' ||
        gt(leverage, maxValue)) &&
      ![ContractType.Tracker, ContractType.Spot].includes(selectedProduct?.contract_type!)
    ) {
      return false;
    }

    // TODO: check if this is needed
    // if (leverageEdit) {
    //   return false;
    // }

    const isTakeProfitValid = isValidTakeProfitValue({
      takeProfitEstPrice,
      orderPrice,
      side,
    });

    if (!isTakeProfitValid) {
      return false;
    }

    return true;
  }
);

export const impactSizeErrorSelector = createSelector(
  [
    selectedProductSelector,
    l2OrderbookSelectorWithDepth,
    sideSelection,
    quantityInputDropdownValue,
    quantityInputValueInContract,
  ],
  (selectedProduct, orderbook, side, dropDownValue, orderSize) => {
    const contractType = selectedProduct?.contract_type;

    // check if dropdown value is quoting symbol
    const quotingSymbol = selectedProduct?.quoting_asset?.symbol;
    const isQuoteAsset = contractType === 'spot' && dropDownValue === quotingSymbol;

    const impactPrice = calculateSpotImpactPrice(
      orderbook,
      side,
      orderSize,
      isQuoteAsset
    );

    const impactSizeError =
      selectedProduct?.contract_type === ContractType.Spot
        ? impactSizeCheckSpot({
            size: orderSize,
            isQuoteAsset,
            price: impactPrice,
            impactSize: selectedProduct.impact_size,
            contractCurrency: selectedProduct.contract_unit_currency,
          })
        : impactSizeCheck(
            orderSize,
            selectedProduct?.impact_size,
            selectedProduct?.contract_value,
            selectedProduct?.contract_unit_currency
          );

    return impactSizeError;
  }
);

export const isDepositSymbolEnabledSelector = createSelector(
  [balanceBySymbolSelector, selectedProductSelector, sideSelection],
  (balanceState, selectedProduct, side) => {
    const isBuyActionState = side === SIDE.BUY;
    let depositSymbol;
    if (selectedProduct?.contract_type === ContractType.Spot) {
      if (isBuyActionState) {
        depositSymbol = selectedProduct?.quoting_asset?.symbol;
      } else {
        depositSymbol = selectedProduct?.underlying_asset?.symbol;
      }
    } else {
      depositSymbol = selectedProduct?.settling_asset?.symbol;
    }

    const isDepositEnabled =
      path([depositSymbol, 'asset', 'deposit_status'], balanceState) ?? false;
    return { isDepositEnabled, depositSymbol };
  }
);

export const confirmationHeaderSelector = createSelector(
  [
    selectedProductSelector,
    quantityInputValueInContract,
    quantityInputDropdownValue,
    orderTypeSelection,
  ],
  (selectedProduct, size, dropDownValue, orderType) => {
    const contractType = selectedProduct?.contract_type;
    const quotingSymbol = selectedProduct?.quoting_asset?.symbol;
    const underlyingSymbol = selectedProduct?.underlying_asset?.symbol;
    const isQuoteAsset = contractType === 'spot' && dropDownValue === quotingSymbol;

    switch (contractType) {
      case 'spot':
        return `${size} ${
          isQuoteAsset ? `${quotingSymbol} ${i18next.t('trading:worthOf')}` : ''
        } ${underlyingSymbol} ${i18next.t('common:at')} ${orderType} ${
          orderType === PLACE_ORDER_TYPE.MARKET ? '' : quotingSymbol
        }`;

      default:
        return `${size} ${generateAssetUnitLabel('', {
          moreThanOne: Number(size) > 1,
        })} ${i18next.t('common:of')} ${selectedProduct?.symbol} ${i18next.t(
          'common:at'
        )} ${orderType.charAt(0).toUpperCase() + orderType.slice(1)}`;
    }
  }
);

export const confirmationTableDataSelector = createSelector(
  [
    selectedProductSelector,
    quantityInputValueInContract,
    quantityInputDropdownValue,
    sideSelection,
    l2OrderbookSelectorWithDepth,
    balanceSelector,
    openPositionState,
    marginValueSelector,
    leverageState,
    estimatedExecPriceSelector,
    userMarginModeSelector,
    tradePreferencesSelector,
    marginValueSelector,
    appliedOffersSelector,
    activeOffersSelector,
    multiCollateralSelector,
    selectedProductBalance,
    orderTypeSelection,
    reduceOnlySelector,
    openingCommissionSelector,
    estimatedTradingFeeSelector,
  ],
  (
    selectedProduct,
    size,
    dropDownValue,
    side,
    orderbook,
    balances,
    position,
    orderMargin,
    leverage,
    estimatedExecPrice,
    marginType,
    tradePreferences,
    estimatedPortfolioMargin,
    appliedOffers,
    activeOffers,
    multiCollateralData,
    productBalance,
    orderType,
    reduceOnly,
    portfolioOpeningCommission,
    estimatedTradingFee
  ) => {
    if (!selectedProduct) {
      return {
        confirmationTable: [],
        priceBandingMsg: '',
      };
    }
    const contractType = selectedProduct?.contract_type;
    const quotingSymbol = selectedProduct?.quoting_asset?.symbol;
    const isQuoteAsset = contractType === 'spot' && dropDownValue === quotingSymbol;
    const isBuyActionState = side === SIDE.BUY;

    /*
            The margin value received from the API includes both opening and closing fees.
            In the confirmation pop-up, we need to display only the margin with the closing fee.
            Therefore, we subtract the opening fee before displaying it.
            Est margin with closing leg fee = (basic margin + closing commision)
            */

    const estimatedMarginWithClosingLegForPortfolio =
      estimatedPortfolioMargin - portfolioOpeningCommission;

    const estimatedOrderMargin = reduceOnly ? 0 : orderMargin + estimatedTradingFee;

    const estimatedPortfolioOpeningTradingFee = round(
      portfolioOpeningCommission,
      selectedProduct?.settling_asset?.minimum_precision || 2
    );

    const precision =
      estimatedMarginWithClosingLegForPortfolio <= 0.01
        ? 4
        : selectedProduct?.settling_asset?.minimum_precision;

    const estimatedPortfolioMarginWithClosingLegFee = round(
      estimatedMarginWithClosingLegForPortfolio,
      precision
    );

    if (contractType === ContractType.Spot) {
      return spotMarketPlaceOrderConfirmationTable({
        selected_product: selectedProduct,
        side,
        orderSize: size,
        isQuoteAsset,
        balances,
        isBuyActionState,
        orderbook,
      });
    }
    const { roundedBalance } = productBalance;

    return marketPlaceOrderConfirmationTable({
      selected_product: selectedProduct,
      position,
      side,
      orderSize: size,
      orderMargin: estimatedOrderMargin,
      isBuyActionState,
      leverage,
      balances,
      estimatedExecPrice,
      marginType,
      vip_discount_factor: tradePreferences.vip_discount_factor,
      estimatedPortfolioMargin: estimatedPortfolioMarginWithClosingLegFee,
      appliedOffers,
      activeOffers,
      multiCollateralData,
      margin_mode: marginType,
      roundedBalance,
      orderBook: orderbook,
      orderType,
      reduceOnly,
      estimatedPortfolioTradingFee: estimatedPortfolioOpeningTradingFee,
    });
  }
);

export const effectiveLeveragePortfolioMarginSelector = createSelector(
  [selectedProductSelector, quantityInputValueInContract, marginValueSelector],
  (selectedProduct, orderSize, margin) => {
    try {
      if (
        orderSize &&
        selectedProduct?.notional_type &&
        selectedProduct?.contract_value
      ) {
        const markPrice: number = markPriceState();
        const spotPrice: number = spotPriceState();

        const notionalInUsd = getNotional(
          selectedProduct?.notional_type,
          selectedProduct?.contract_value,
          Number(orderSize),
          isOptions(selectedProduct?.contract_type) ? spotPrice : markPrice
        );
        const effectiveLeverage = division(Number(notionalInUsd), Number(margin));
        return effectiveLeverage;
      }
      return null;
    } catch (error) {
      console.error('Error calculating effective leverage:', error);
      return null;
    }
  }
);

export const quantityDropdownValueSelector = createSelector(
  [primaryCurrencySelector, secondaryCurrencySelector, selectedProductSelector],
  (primaryCurrency, secondaryCurrency, selectedProduct) => {
    const contractType = selectedProduct?.contract_type;
    const values: string[] = [];

    if (isSpotContract(contractType)) {
      if (primaryCurrency) {
        values.push(primaryCurrency);
      }
      if (secondaryCurrency) {
        values.push(secondaryCurrency);
      }

      return values;
    }

    values.push(UNIT_OF_ASSETS_QUANTITY);
    if (secondaryCurrency) {
      values.push(secondaryCurrency);
    }

    if (primaryCurrency) {
      values.push(primaryCurrency);
    }

    return values;
  }
);

// TODO: Update for different order types
export const generateMixPanelDataOnOrderSubmission = createSelector(
  [selectedProductSelector, quantityInputValueInContract, userMarginModeSelector],
  (selectedProduct, orderSize, marginMode) => {
    const markPrice = markPriceState();
    const mixpanelData = {
      notional: Number(orderSize) * Number(selectedProduct?.contract_value),
      price: markPrice,
      quantity: orderSize,
      marginType: marginMode,
      source: 'Single Order',
    };

    return mixpanelData;
  }
);

export const hintTextSelector = createSelector(
  [
    allConvertedCurrencyValuesSelector,
    quantityInputDropdownValue,
    selectedProductSelector,
    quantityInputValue,
    quantityDropdownValueSelector,
  ],
  hintTextToShowInQuantityInput
);

// enabled for market place order form only for now
export const isMarketOrderEnabledSelector = createSelector(
  [orderTypeSelection, ({ user }) => isAuthenticated(user)],
  (orderType, isLoggedIn) => isLoggedIn && [PLACE_ORDER_TYPE.MARKET].includes(orderType)
);

export const isPlaceOrderV2EnabledSelector = createSelector(
  [orderTypeSelection, ({ user }) => isAuthenticated(user)],
  (orderType, isLoggedIn) => isLoggedIn && [PLACE_ORDER_TYPE.MARKET].includes(orderType)
);
